import React from 'react'
import { Box, Button, Card, CardActionArea, IconButton, Stack } from '@mui/material'

import { P, Text } from '@mxenabled/mxui'
import { Close } from '@mxenabled/mx-icons'

interface MicroCardTemplateProps {
  ariaLabel?: string
  closeAriaLabel?: string
  description: string
  elevation?: number
  icon?: React.ReactNode
  isActiveCard?: boolean
  heightOverrides?: string | number
  onClose?: () => void
  onCardClick?: () => void
  onPrimaryAction?: () => void
  onSecondaryAction?: () => void
  primaryActionText?: string
  secondaryActionText?: string
  title: string
  variant?: 'outlined' | 'elevation' | undefined
  widthOverrides?: string | number
}

const MicroCardTemplate = React.forwardRef<HTMLDivElement, MicroCardTemplateProps>(
  function MicroCardTemplate(
    {
      ariaLabel,
      closeAriaLabel,
      description,
      elevation,
      icon,
      isActiveCard,
      heightOverrides,
      onClose,
      onCardClick,
      onPrimaryAction,
      onSecondaryAction,
      primaryActionText,
      secondaryActionText,
      title,
      variant,
      widthOverrides,
      ...restProps
    },
    inViewRef,
  ) {
    const hasPrimaryAction = Boolean(onPrimaryAction && primaryActionText)
    const hasSecondaryAction = Boolean(onSecondaryAction && secondaryActionText)

    const innerContent = (
      <>
        {onClose && (
          <IconButton
            aria-label={closeAriaLabel}
            onClick={(e) => {
              e.stopPropagation()
              onClose?.()
            }}
            onMouseDown={(e) => {
              e.stopPropagation()
            }}
            onTouchStart={(e) => {
              e.stopPropagation()
            }}
            sx={{ minHeight: 40, minWidth: 40, position: 'absolute', right: 4, top: 4 }}
          >
            <Close />
          </IconButton>
        )}
        <Stack sx={{ gap: 6, height: '100%', justifyContent: 'space-between' }}>
          <Stack sx={{ flexDirection: 'row', gap: 12 }}>
            {icon}
            <Stack sx={{ gap: 6 }}>
              <Stack sx={{ flexDirection: 'row', justifyContent: 'space-between' }}>
                <P bold={true} sx={{ lineHeight: '16px' }} variant="body1">
                  {title}
                </P>
              </Stack>
              <P
                dangerouslySetInnerHTML={{ __html: description }}
                sx={{
                  display: '-webkit-box',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  WebkitBoxOrient: 'vertical',
                  WebkitLineClamp: hasPrimaryAction ? 2 : 3,
                  '& .bold-copy': {
                    fontWeight: 'bold',
                    fontFamily: 'inherit',
                  },
                }}
                variant="subtitle2"
              />
            </Stack>
          </Stack>

          {hasPrimaryAction && (
            <Stack
              sx={{
                alignItems: 'center',
                flexDirection: 'row',
                height: 28,
                pb: 4,
                pl: icon ? 36 : 0,
              }}
            >
              {hasPrimaryAction && (
                <Button
                  onClick={(e) => {
                    e.stopPropagation()
                    onPrimaryAction?.()
                  }}
                  onMouseDown={(e) => {
                    e.stopPropagation()
                  }}
                  onTouchStart={(e) => {
                    e.stopPropagation()
                  }}
                  size="small"
                  sx={{ px: 8, ml: icon ? 0 : -8 }}
                  variant="text"
                >
                  <Text bold={true} variant="body2">
                    {primaryActionText}
                  </Text>
                </Button>
              )}

              {hasSecondaryAction && (
                <Button
                  onClick={(e) => {
                    e.stopPropagation()
                    onSecondaryAction?.()
                  }}
                  onMouseDown={(e) => {
                    e.stopPropagation()
                  }}
                  onTouchStart={(e) => {
                    e.stopPropagation()
                  }}
                  size="small"
                  sx={{ px: 8 }}
                  variant="text"
                >
                  <Text bold={true} sx={{ color: 'text.secondary' }} variant="body2">
                    {secondaryActionText}
                  </Text>
                </Button>
              )}
            </Stack>
          )}
        </Stack>
      </>
    )

    return (
      <Card
        aria-hidden={!isActiveCard}
        aria-label={ariaLabel}
        elevation={elevation}
        ref={inViewRef}
        role="group"
        sx={{
          backgroundColor: 'background.paper',
          height: heightOverrides ? '100%' : 120,
          minWidth: widthOverrides ? `${widthOverrides}px` : '100%',
          position: 'relative',
          my: 1,
          borderRadius: 2,
        }}
        tabIndex={onCardClick || !isActiveCard ? -1 : 0}
        variant={variant}
        {...restProps}
      >
        {onCardClick ? (
          <CardActionArea
            component="div"
            onClick={onCardClick}
            sx={{
              pb: 8,
              pt: 16,
              px: 16,
              height: '100%',
            }}
          >
            {innerContent}
          </CardActionArea>
        ) : (
          <Box sx={{ pb: 8, pt: 16, px: 16, height: '100%' }}>{innerContent}</Box>
        )}
      </Card>
    )
  },
)

export default MicroCardTemplate
